import { type MouseEvent, useCallback, useMemo } from 'react';

import { fireCustomCrossAppAnalytic } from '@atlassiansox/analytics-cross-product-interaction-client/fire-cross-product-analytic';

// This is a subpath import rather than the package root because
// `@atlaskit/analytics-next`'s root is a prohibited barrel import.
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { DEFAULT_GAS_CHANNEL } from '../../constants/analytics';
import { isAuxClick } from '../clicks';
import { parseInteractionSession } from '../parse-interaction-session';

/**
 * One id for every search result link. `bridge` (from the decoded param) already
 * separates the Search Page from Quick Find, and `destinationApp` already says
 * where the link goes, so a per-surface id would mostly duplicate those while
 * forcing consumers to know the full set of values.
 */
const ACTION_SUBJECT_ID = 'searchResultLink';

export type CrossAppLinkHandlers = {
	onClick: () => void;
	/** Middle-click fires `auxclick`, not `click`, so anchors need both. */
	onAuxClick: (event: MouseEvent<HTMLElement>) => void;
};

/**
 * Handlers for a result anchor that report the cross-app interaction analytic,
 * built from the `xpis` param already on the href.
 *
 * URL wrapping remains the primary cross-product tracking mechanism; this is a
 * supplementary signal for the cases where the destination side drops the
 * attribution (a product missing from the client's `validProducts` allow-list, a
 * redirect stripping the param, session storage unavailable). Because the payload
 * is decoded from the link itself, it always agrees with what the URL carries, and
 * an unwrapped link reports nothing at all.
 *
 * Attach to the anchor, NOT to a containing row or heading. On the Jira table rows
 * the row-level `onClick` also fires for the summary, status, assignee and empty
 * cells, none of which navigate, so a container-level analytic would over-count.
 * The row's own `advancedSearchResultLink clicked` event is a different event and
 * is unaffected.
 *
 * Note the decoded interaction session `id` is deliberately not reported:
 * `fireCustomCrossAppAnalytic` emits a fixed `{ bridge, source, destinationApp }`
 * attribute set with no passthrough, so there is nowhere to put it.
 *
 * @example
 * const crossAppLinkHandlers = useCrossAppLinkHandlers({
 *   href: wrappedUrl,
 *   destinationApp: product,
 * });
 * return <Anchor href={wrappedUrl} {...crossAppLinkHandlers} />;
 */
export const useCrossAppLinkHandlers = ({
	href,
	destinationApp,
}: {
	/** The final href the anchor renders, including `xpis` if it was wrapped. */
	href: string | undefined;
	/** The product the link navigates to, e.g. `confluence`. */
	destinationApp: string | undefined;
}): CrossAppLinkHandlers => {
	// `fireCustomCrossAppAnalytic` needs the raw `createAnalyticsEvent`, not the
	// `useAnalytics` controller wrapper, so this hook owns the analytics-next
	// dependency. It lives in `common/utils` rather than `controllers/events`
	// because `common/ui` consumes it and must not depend on `controllers`.
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireCrossAppAnalytic = useCallback(() => {
		if (!destinationApp) {
			return;
		}

		const interactionSession = parseInteractionSession(href);

		// No usable `xpis` means the link was never wrapped, so there is no
		// cross-product interaction to report.
		if (!interactionSession) {
			return;
		}

		fireCustomCrossAppAnalytic({
			tags: ['navigationExperiences'],
			analyticsEventFireName: DEFAULT_GAS_CHANNEL,
			destinationApp,
			bridge: interactionSession.bridge,
			source: interactionSession.source,
			actionSubjectId: ACTION_SUBJECT_ID,
			createAnalyticsEvent,
		});
	}, [href, destinationApp, createAnalyticsEvent]);

	return useMemo(
		() => ({
			onClick: fireCrossAppAnalytic,
			// `auxclick` covers middle-click (button 1) but also right-click (button 2),
			// and opening the context menu is not a navigation, so it must be filtered.
			onAuxClick: (event: MouseEvent<HTMLElement>) => {
				if (isAuxClick(event)) {
					fireCrossAppAnalytic();
				}
			},
		}),
		[fireCrossAppAnalytic],
	);
};
